import { supabase } from '../lib/supabase';

export const fundraiserService = {
  // Create fundraiser
  async createFundraiser(fundraiserData) {
    try {
      const { data, error } = await supabase?.from('fundraisers')?.insert([fundraiserData])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get organization's fundraisers
  async getOrganizationFundraisers(organizationId) {
    try {
      const { data, error } = await supabase?.from('fundraisers')?.select(`
          *,
          donations(count),
          organizations!inner(name, logo_url, is_501c3)
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get active public fundraisers
  async getActiveFundraisers(filters = {}) {
    try {
      let query = supabase?.from('fundraisers')?.select(`
          *,
          organizations!inner(name, industry, logo_url, is_501c3, verified_at)
        `)?.eq('status', 'active')?.not('organizations.verified_at', 'is', null);

      if (filters?.is_501c3 !== undefined) {
        query = query?.eq('organizations.is_501c3', filters?.is_501c3);
      }

      if (filters?.industry) {
        query = query?.eq('organizations.industry', filters?.industry);
      }

      if (filters?.min_amount) {
        query = query?.gte('goal_amount', filters?.min_amount);
      }

      if (filters?.max_amount) {
        query = query?.lte('goal_amount', filters?.max_amount);
      }

      const { data, error } = await query?.order('created_at', { ascending: false })?.limit(20);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get fundraiser by ID with details
  async getFundraiser(fundraiserId) {
    try {
      const { data, error } = await supabase?.from('fundraisers')?.select(`
          *,
          organizations!inner(
            name, legal_name, description, website, industry, 
            logo_url, is_501c3, verified_at, address_line1, 
            city, state, country
          ),
          donations(
            id, amount, currency, donor_message, is_anonymous, created_at,
            user_profiles!inner(full_name)
          )
        `)?.eq('id', fundraiserId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update fundraiser
  async updateFundraiser(fundraiserId, updates) {
    try {
      const { data, error } = await supabase?.from('fundraisers')?.update(updates)?.eq('id', fundraiserId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Delete fundraiser
  async deleteFundraiser(fundraiserId) {
    try {
      const { error } = await supabase?.from('fundraisers')?.delete()?.eq('id', fundraiserId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Make donation to fundraiser
  async makeDonation(donationData) {
    try {
      const { data, error } = await supabase?.from('donations')?.insert([{
          ...donationData,
          status: 'pending' // Will be updated by payment processor
        }])?.select(`
          *,
          fundraisers!inner(title, is_tax_deductible),
          financial_accounts!inner(account_name)
        `)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get user's donations
  async getUserDonations(userId) {
    try {
      const { data, error } = await supabase?.from('donations')?.select(`
          *,
          fundraisers!inner(
            title, organizations!inner(name, logo_url, is_501c3)
          ),
          tax_receipts(receipt_number, receipt_date, tax_deductible_amount)
        `)?.eq('donor_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get fundraiser donations (for organization owners)
  async getFundraiserDonations(fundraiserId) {
    try {
      const { data, error } = await supabase?.from('donations')?.select(`
          *,
          user_profiles!inner(full_name, email)
        `)?.eq('fundraiser_id', fundraiserId)?.eq('status', 'completed')?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Generate tax receipt
  async generateTaxReceipt(donationId) {
    try {
      const { data, error } = await supabase?.rpc('generate_tax_receipt', { donation_uuid: donationId });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};